'use client';

import { useRef } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import type { PreorderItem } from '@/lib/api';
import { usePriceConvert } from '@/lib/PriceContext';
import { useT } from '@/hooks/useT';
import { stagger, staggerItem, fadeUp, viewportOnce } from '@/v4/theme/motion';

const PL_TAG: Record<string, { label: string; cls: string }> = {
  XBOXSERIESX: { label: 'X|S',  cls: 'ptag-xsx' },
  XBOXONE:     { label: 'ONE',  cls: 'ptag-xone' },
  PC:          { label: 'PC',   cls: 'ptag-pc' },
};

const DATE_LOCALE: Record<string, string> = { uk: 'uk-UA', ru: 'ru-RU', en: 'en-US' };

function fmtPrice(price: number | null, cur: string | null) {
  if (!price) return null;
  return new Intl.NumberFormat('uk-UA', {
    style: 'currency', currency: cur || 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(price);
}

function fmtDateShort(d: string, lang: string) {
  return new Date(d).toLocaleDateString(DATE_LOCALE[lang] ?? 'en-US', { day: 'numeric', month: 'short' });
}

function truncate(s: string | null | undefined, max = 120): string {
  if (!s) return '';
  return s.length > max ? s.slice(0, max).trimEnd() + '…' : s;
}

function PoCard({ item }: { item: PreorderItem }) {
  const { convert: fmtConverted, lang } = usePriceConvert();
  const price    = fmtConverted(item.min_key_usd ?? item.list_price) ?? fmtPrice(item.min_key_usd ?? item.list_price, item.currency_code);
  const discount = item.discount_pct && item.discount_pct > 0 ? Math.round(item.discount_pct) : null;
  const platforms = item.platforms.map(p => PL_TAG[p]).filter(Boolean) as { label: string; cls: string }[];
  const desc = item.short_description || truncate(item.description_en);

  return (
    <Link href={`/product/${item.product_id}`} className="v4po">
      <div className="v4po-art">
        {item.poster
          ? <img src={item.poster} alt={item.title} loading="lazy" />
          : null
        }

        <span className="v4po-date">
          <CalendarDays size={9} aria-hidden />
          {fmtDateShort(item.release_at, lang)}
        </span>

        {discount && <span className="v4po-disc">-{discount}%</span>}

        {platforms.length > 0 && (
          <div className="v4po-plats">
            {platforms.map(p => (
              <span key={p.label} className="v4po-plat">{p.label}</span>
            ))}
          </div>
        )}
      </div>

      <div className="v4po-body">
        <p className="v4po-title">{item.title}</p>
        <p className="v4po-desc">{desc}</p>
        <div className="v4po-footer">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            {price
              ? <span className="v4po-price">{price}</span>
              : <span className="v4po-tba">TBA</span>
            }
            {discount != null && item.msrp != null && (
              <span className="v4po-msrp">{fmtConverted(item.msrp) ?? fmtPrice(item.msrp, item.currency_code)}</span>
            )}
          </div>
          <ChevronRight size={16} color="var(--v4-fg-4)" />
        </div>
      </div>
    </Link>
  );
}

interface Props {
  title: string;
  href: string;
  items: PreorderItem[];
  eyebrow?: string;
}

export function PreorderCarousel({ title, href, items, eyebrow }: Props) {
  const t = useT();
  const scrollRef = useRef<HTMLDivElement>(null);

  function scroll(dir: 'left' | 'right') {
    scrollRef.current?.scrollBy({ left: dir === 'right' ? 1080 : -1080, behavior: 'smooth' });
  }

  if (!items.length) return null;

  return (
    <motion.section {...fadeUp()}>
      <div className="v4home-sec-hd">
        <div>
          {eyebrow && <p className="v4home-eyebrow">{eyebrow}</p>}
          <h2 className="v4home-sec-title">{title}</h2>
        </div>
        <Link href={href} className="v4home-sec-link">
          {t('common_see_all')} <ChevronRight size={13} strokeWidth={2.5} />
        </Link>
      </div>

      <div className="v4home-car-wrap">
        <button
          className="v4home-car-arrow v4home-car-arrow--l"
          onClick={() => scroll('left')}
          aria-label={t('hero_prev')}
        >
          <ChevronLeft size={18} />
        </button>

        <motion.div
          ref={scrollRef}
          className="v4home-car-track"
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {items.map(item => (
            <motion.div key={item.product_id} variants={staggerItem} className="v4home-car-cell v4home-car-cell--po">
              <PoCard item={item} />
            </motion.div>
          ))}
        </motion.div>

        <button
          className="v4home-car-arrow v4home-car-arrow--r"
          onClick={() => scroll('right')}
          aria-label={t('hero_next')}
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </motion.section>
  );
}
